"use client";
import React, { useEffect, useMemo, useState } from "react";
import { calculateReadTime } from "@/utils/ReadTime";
import {
  extractImageFromDescription,
  removeFirstImageFromDescription,
} from "@/utils/helpers/extract";
import {
  buildTocAndHtml,
  formatReadTime,
  getCategory,
} from "./articleHelpers";
import ArticleTOC from "./ArticleTOC";
import RelatedArticles from "./RelatedArticles";
import ShareModal from "./ShareModal";
import ArticleHero from "./ArticleHero";
import ArticleMeta from "./ArticleMeta";
import ArticleByline from "./ArticleByline";
import ArticleBody from "./ArticleBody";
import ArticleFaqs from "./ArticleFaqs";
import ArticleTags from "./ArticleTags";

const SingleBlogPage = ({ blog, allBlogs = [] }) => {
  const post = blog?.blog;

  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [activeId, setActiveId] = useState(null);

  // Resolve the current URL on the client (avoids SSR mismatch)
  useEffect(() => {
    setShareUrl(window.location.href);
  }, []);

  // 5 most recent posts, excluding the current one
  const recentBlogs = useMemo(() => {
    if (!allBlogs?.length) return [];
    return [...allBlogs]
      .filter((b) => b?.slug !== post?.slug)
      .sort(
        (a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0),
      )
      .slice(0, 5);
  }, [allBlogs, post?.slug]);

  const heroImage =
    post?.image || extractImageFromDescription(post?.description);

  const rawDescription =
    heroImage && heroImage === extractImageFromDescription(post?.description)
      ? removeFirstImageFromDescription(post?.description)
      : post?.description;

  const cleanedDescription = rawDescription
    ?.replace(/&nbsp;/g, " ")
    .replace(/ /g, " ");

  const readTime = useMemo(
    () => calculateReadTime(cleanedDescription ?? "", 200, 5),
    [cleanedDescription],
  );

  // Bake heading ids into the HTML and collect the TOC (pure, SSR-safe)
  const { html: contentHtml, toc: tocItems } = useMemo(
    () => buildTocAndHtml(cleanedDescription),
    [cleanedDescription],
  );

  // Highlight the section currently in view
  useEffect(() => {
    if (!tocItems.length) return;
    const headings = tocItems
      .map((item) => document.getElementById(item.id))
      .filter(Boolean);
    if (!headings.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "0px 0px -75% 0px", threshold: 0 },
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [tocItems]);

  const relatedPosts = useMemo(
    () =>
      recentBlogs.map((p) => ({
        id: p?.id,
        slug: p?.slug,
        title: p?.title || "Untitled",
        image: p?.image || extractImageFromDescription(p?.description),
        category: getCategory(p),
        readTime: formatReadTime(p?.description),
      })),
    [recentBlogs],
  );

  if (!blog) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            No Blog Data Found
          </h1>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const formattedDate = post?.created_at
    ? new Date(post.created_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  const category = getCategory(post);
  const tags = [
    ...new Set(
      (post?.meta_keywords || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    ),
  ].slice(0, 6);

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-[1240px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[200px_minmax(0,1fr)_280px] gap-8 lg:gap-10">
          {/* LEFT — In This Article */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <ArticleTOC items={tocItems} activeId={activeId} />
            </div>
          </aside>

          {/* CENTER — Article */}
          <article className="min-w-0">
            <ArticleHero src={heroImage} alt={post?.title} />

            <ArticleMeta
              category={category}
              date={formattedDate}
              readTime={readTime}
            />

            {post?.title && (
              <h1 className="text-3xl md:text-[40px] font-bold tracking-tight leading-[1.15] text-gray-900 mb-6">
                {post.title}
              </h1>
            )}

            <ArticleByline
              date={formattedDate}
              onShare={() => setShareOpen(true)}
            />

            <ArticleBody html={contentHtml} />

            <ArticleFaqs faqs={post?.faqs} />

            <ArticleTags tags={tags} />

            {/* Related — shown inline on small screens */}
            <div className="lg:hidden mt-12">
              <RelatedArticles posts={relatedPosts} />
            </div>
          </article>

          {/* RIGHT — Related Articles */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <RelatedArticles posts={relatedPosts} />
            </div>
          </aside>
        </div>
      </div>

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        url={shareUrl}
        title={post?.title}
      />
    </div>
  );
};

export default SingleBlogPage;
